import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { TopBar } from "@/components/TopBar";
import { motion } from "framer-motion";
import {
  ArrowLeft, Printer, LogIn, LayoutDashboard, BookOpen, Volume2, Layers, HelpCircle,
  CheckCircle2, BookHeart, ListChecks, CalendarDays, Award, Flame, KeyRound, Gamepad2,
  Sparkles, Cross, Star, Tv, Timer, Trophy,
} from "lucide-react";

const ICON = "https://static.prod-images.emergentagent.com/jobs/aa10c7d5-1915-4689-8d0f-1ed7e74b35ba/images";
const OWL = `${ICON}/35a77659cf1ca765a27da264e8f8eb35696a86c1b2741723b108ada140975251.jpeg`;
const BADGES = [
  { name: "First Steps", desc: "Finish your very first assignment", img: `${ICON}/9750fe9198823a9028b2c88dd45b4ffd490aa27fecfae13307f73614f7a15b56.jpeg` },
  { name: "Bookworm", desc: "Finish 5 assignments", img: `${ICON}/ca7d51607f2b98d2b12879106cd345c19769401c8537669e5fe1503093c97a4e.jpeg` },
  { name: "Scripture Scholar", desc: "Memorize your first Bible verse", img: `${ICON}/41519d61d5dae009ac772c8be51bb907fff4896a36ae4fe5081f3562ed7770f7.jpeg` },
  { name: "Faithful Reader", desc: "Do a day of your Bible reading plan", img: `${ICON}/212ab9f3a6bc0dbc4f2fa50673f40f766c2197d6c7120d7710ad7867525b7882.jpeg` },
  { name: "On Fire", desc: "Read 3 days in a row (a streak!)", img: `${ICON}/40a72be81ae18fb4d67fdf6bb3d1380d972cb88ce3099de3d7c1acdabf542d8f.jpeg` },
  { name: "Bright Star", desc: "Score 90% or higher", img: `${ICON}/52ed39ac1ba7ea48007be1d96514ae6f155ef6b5c72f38b3cda582f555ef7d5f.jpeg` },
  { name: "Scientist", desc: "Finish a Science assignment", img: `${ICON}/a2156b08839757be132390efef720fbc2b73c686b6f312071fae2fd43a0c616f.jpeg` },
  { name: "Explorer", desc: "Finish a Social Studies assignment", img: `${ICON}/42c0875c75cd095c18be15c0378abae08cc43a2c49afc550ec180aeca015ead7.jpeg` },
  { name: "Game On", desc: "Play a learning mini-game", img: `${ICON}/50b3b5c830a96f05b9f16a45e4c5dda372d2fe9afdec9ea18fc7eb0e67a621e0.jpeg` },
];

const LESSON_STEPS = [
  { icon: BookOpen, color: "#81B29A", title: "1. Open a lesson", text: "On your dashboard, find an assignment card and tap START (or CONTINUE). This opens the lesson so you can read it." },
  { icon: Volume2, color: "#E07A5F", title: "2. Read it — or listen", text: "Read the summary, Key Facts, and vocabulary. Want it read out loud? Tap \"Read lesson aloud.\" You can pick your own narrator voice at the bottom of your dashboard." },
  { icon: Layers, color: "#F4A261", title: "3. Study with flashcards", text: "Tap \"Study flashcards\" to practice the vocabulary and Key Facts. Tap a card to flip it and check yourself. Tap \"Next card\" to keep going." },
  { icon: HelpCircle, color: "#3D405B", title: "4. Stuck? Ask your tutor", text: "Tap the \"I don't understand this\" button. Your friendly AI tutor gives hints, examples, easier explanations, or can quiz you — but it never just hands you the answers." },
  { icon: CheckCircle2, color: "#81B29A", title: "5. Answer & submit", text: "Tap \"Begin assignment.\" Answer one question at a time and tap Next. On the last one, tap Submit. You'll see your score and helpful feedback right away!" },
];

const FEATURES = [
  { icon: ListChecks, color: "#E07A5F", title: "Chores", text: "Your parent may assign chores as part of school. Open Chores, do each one, then tap the circle to check it off. Some chores are 'Up for grabs' — any kid can do them, and the first to tap 'Claim & complete' wins the reward! Finished chores are worth 100 points and screen-time minutes." },
  { icon: Tv, color: "#3D405B", title: "Screen Time", text: "Every chore you finish adds minutes to your Screen-Time balance. Open Screen Time to see how much you've earned. Your parent logs it when you use it on phone or TV." },
  { icon: Gamepad2, color: "#F4A261", title: "Mini-Games", text: "Play learning games built from your own lessons — Vocabulary Match, Timed Quiz, Spelling Bee and Fill in the Blank. Play any game to earn the Game On badge!" },
  { icon: Trophy, color: "#E07A5F", title: "Leaderboard", text: "See this week's family leaderboard! Earn points for chores, lessons and games. It resets every Monday, so keep working hard." },
  { icon: BookHeart, color: "#81B29A", title: "Scripture Memory", text: "Learn Bible verses (NIV). Tap \"I know it!\" when you've memorized one, and use \"Quiz me with flashcards\" to practice. Memorize on different days to grow a streak." },
  { icon: BookOpen, color: "#3D405B", title: "Bible Reading Plan", text: "Read a little of God's Word each day and tap the day to check it off. Read several days in a row to build a reading streak. Don't break the chain!" },
  { icon: CalendarDays, color: "#E07A5F", title: "School Calendar", text: "See what's coming up. Your assignments show up on the days they're due, so you always know what's next." },
  { icon: Award, color: "#81B29A", title: "Badges", text: "Earn fun badges just for showing up and doing your work. See all your badges on the My Badges page and try to collect all 9!" },
  { icon: Flame, color: "#F4A261", title: "Streaks", text: "A streak counts the days in a row you keep going — in reading and scripture. The longer your streak, the bigger the win. Keep it burning!" },
];

export default function StudentManual() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isParent = user?.role === "Parent";

  return (
    <div>
      <div className="no-print"><TopBar subtitle="Student Manual" /></div>
      <style>{`@media print {
        .no-print { display: none !important; }
        body { background: #fff !important; }
        .manual-card { break-inside: avoid; box-shadow: none !important; }
        @page { margin: 14mm; }
      }`}</style>

      <main className="max-w-3xl mx-auto px-6 md:px-8 py-10">
        <div className="no-print flex items-center justify-between mb-6">
          <button onClick={() => navigate(isParent ? "/parent" : "/student")} className="flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#3D405B]"><ArrowLeft size={16} /> Back</button>
          <button data-testid="manual-print-btn" onClick={() => window.print()}
            className="flex items-center gap-2 rounded-full bg-[#3D405B] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#2f3248] transition-colors">
            <Printer size={16} /> Print / Save as PDF
          </button>
        </div>

        {/* Cover */}
        <div className="manual-card relative grain rounded-3xl overflow-hidden p-8 md:p-10 text-white mb-10" style={{ background: "#81B29A" }}>
          <div className="flex items-center gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-2 text-white/80"><Sparkles size={16} /><span className="text-xs font-semibold uppercase tracking-[0.2em]">Student Manual</span></div>
              <h1 className="font-head text-4xl md:text-5xl font-bold mt-2">Your Rising Force Academy Guide</h1>
              <p className="mt-3 text-white/90 max-w-md leading-relaxed">Hi{isParent ? "" : `, ${user?.first_name}`}! I'm <b>Ollie the Owl</b>, and I'll be your guide. This handbook shows you everything you can do — how to sign in, do your lessons, and have a little fun along the way. 😊</p>
            </div>
            <img src={OWL} alt="Ollie the Owl mascot" className="w-32 md:w-44 shrink-0 rounded-3xl shadow-lg ring-4 ring-white/40" />
          </div>
        </div>

        {/* Signing in */}
        <Heading icon={LogIn} title="Signing in" />
        <div className="manual-card rounded-2xl bg-white border border-[#EAE6DF] p-6 mb-10">
          <ol className="list-decimal pl-5 space-y-2 text-[#3D405B]">
            <li>Open your family's <b>Rising Force Academy</b> link in a web browser (your parent has it).</li>
            <li>Type your <b>email</b> and <b>password</b> exactly as your parent gave you.</li>
            <li>Tap <b>Sign in</b>. You'll land on your very own student dashboard.</li>
          </ol>
          <div className="mt-4 flex items-start gap-3 rounded-xl bg-[#F2EFE9] p-4 text-sm text-[#3D405B]">
            <KeyRound size={16} className="mt-0.5 shrink-0 text-[#E07A5F]" />
            <span>Want to change your password? Tap <b>Password</b> at the top of any page. Forgot it? Just ask your parent — they can set a new one for you.</span>
          </div>
        </div>

        {/* Dashboard tour */}
        <Heading icon={LayoutDashboard} title="Your dashboard" />
        <div className="manual-card rounded-2xl bg-white border border-[#EAE6DF] p-6 mb-10">
          <p className="text-[#3D405B] leading-relaxed">When you sign in you'll see:</p>
          <ul className="list-disc pl-5 space-y-1.5 mt-2 text-[#3D405B]">
            <li>A friendly <b>good-morning greeting</b> and today's <b>Verse of the Day</b>.</li>
            <li><b>Reminders</b> for anything due today or tomorrow.</li>
            <li>Quick buttons for <b>My Badges</b>, <b>Scripture Memory</b>, <b>Reading Plan</b>, <b>Chores</b>, <b>Mini-Games</b>, and the <b>Calendar</b>.</li>
            <li>Your list of <b>assignments</b> — tap one to get started.</li>
            <li>A <b>voice picker</b> at the bottom so you can choose who reads lessons out loud to you.</li>
          </ul>
        </div>

        {/* Doing a lesson */}
        <Heading icon={BookOpen} title="Doing a lesson, step by step" />
        <div className="space-y-3 mb-10">
          {LESSON_STEPS.map((s, i) => (
            <motion.div key={i} className="manual-card flex items-start gap-4 rounded-2xl bg-white border border-[#EAE6DF] p-5"
              initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.04 }}>
              <span className="grid place-items-center w-11 h-11 rounded-2xl text-white shrink-0" style={{ background: s.color }}><s.icon size={20} /></span>
              <div><p className="font-head font-semibold text-[#3D405B]">{s.title}</p><p className="text-[#6B7280] text-sm mt-0.5 leading-relaxed">{s.text}</p></div>
            </motion.div>
          ))}
        </div>

        {/* Features */}
        <Heading icon={Star} title="Everything else you can do" />
        <div className="grid sm:grid-cols-2 gap-3 mb-10">
          {FEATURES.map((s, i) => (
            <div key={i} className="manual-card rounded-2xl bg-white border border-[#EAE6DF] p-5">
              <span className="grid place-items-center w-10 h-10 rounded-2xl text-white mb-3" style={{ background: s.color }}><s.icon size={18} /></span>
              <p className="font-head font-semibold text-[#3D405B]">{s.title}</p>
              <p className="text-[#6B7280] text-sm mt-0.5 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>

        {/* Badges */}
        <div className="manual-card rounded-3xl p-8 text-white mb-10 grain relative overflow-hidden" style={{ background: "#3D405B" }}>
          <div className="flex items-center gap-2 text-white/80 mb-1"><Award size={18} /><span className="text-xs font-semibold uppercase tracking-[0.2em]">Earn Badges</span></div>
          <h2 className="font-head text-3xl font-bold">Collect all 9 badges!</h2>
          <p className="text-white/85 mt-1 mb-6">You earn badges just for showing up and doing your work. Here's how to get each one:</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {BADGES.map((b) => (
              <div key={b.name} className="text-center bg-white/10 rounded-2xl p-3">
                <img src={b.img} alt={b.name} className="w-16 h-16 mx-auto object-contain" />
                <p className="font-head font-semibold text-sm mt-2">{b.name}</p>
                <p className="text-white/75 text-xs mt-0.5">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Mini-games */}
        <Heading icon={Gamepad2} title="Mini-games" />
        <div className="manual-card rounded-2xl bg-white border border-[#EAE6DF] p-6 mb-10">
          <div className="flex items-start gap-4">
            <img src={OWL} alt="Ollie the Owl" className="w-16 shrink-0 hidden sm:block rounded-xl" />
            <div>
              <p className="text-[#3D405B] leading-relaxed">Open <b>Mini-Games</b> from your dashboard to play games made from your very own lessons:</p>
              <ul className="mt-3 space-y-2 text-[#3D405B]">
                <li className="flex items-start gap-2"><Layers size={16} className="mt-0.5 shrink-0 text-[#81B29A]" /> <span><b>Vocabulary Match</b> — tap a word, then tap its meaning. Match them all as fast as you can!</span></li>
                <li className="flex items-start gap-2"><Timer size={16} className="mt-0.5 shrink-0 text-[#E07A5F]" /> <span><b>Timed Quiz</b> — beat the clock and answer as many questions as you can before time runs out.</span></li>
                <li className="flex items-start gap-2"><Layers size={16} className="mt-0.5 shrink-0 text-[#3D405B]" /> <span><b>Spelling Bee</b> — hear the word and read its clue, then type the correct spelling.</span></li>
                <li className="flex items-start gap-2"><Layers size={16} className="mt-0.5 shrink-0 text-[#F4A261]" /> <span><b>Fill in the Blank</b> — read the meaning and pick the word that fits.</span></li>
              </ul>
              <p className="mt-3 text-sm text-[#6B7280] flex items-center gap-2"><Gamepad2 size={15} className="text-[#F4A261]" /> Play any game to earn the <b>Game On</b> badge — and watch for more games coming soon!</p>
            </div>
          </div>
        </div>

        {/* Verse footer */}
        <div className="manual-card rounded-2xl bg-[#F2EFE9] border border-[#EAE6DF] p-6 text-center mb-6">
          <Cross size={18} className="mx-auto text-[#E07A5F]" />
          <p className="mt-2 text-[#3D405B] leading-relaxed">Most of all, have fun learning and growing! "Whatever you do, work at it with all your heart, as working for the Lord." <span className="stat text-sm text-[#6B7280]">— Colossians 3:23 (NIV)</span></p>
        </div>

        {!isParent && (
          <button data-testid="manual-start-btn" onClick={() => navigate("/student")}
            className="no-print w-full rounded-full bg-[#81B29A] text-white py-3.5 font-semibold hover:bg-[#6f9d87] transition-colors">
            Got it — let's learn! 🎉
          </button>
        )}
      </main>
    </div>
  );
}

function Heading({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <span className="grid place-items-center w-9 h-9 rounded-xl bg-[#3D405B] text-white"><Icon size={17} /></span>
      <h2 className="font-head text-2xl font-bold text-[#3D405B]">{title}</h2>
    </div>
  );
}
